import { useEffect, useRef, useState } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { Button, Collapse, message, Skeleton } from 'antd';
import {
    ArrowRightOutlined,
    CheckOutlined,
    FileTextOutlined,
    GiftOutlined,
    ShoppingOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import { get, post } from '../../axios/axios';
import { Nav } from '../../components/Nav';
import GroupBuyingOrder from '../../components/GroupBuyingOrder/GroupBuyingOrder';
import { rememberPackageOrder } from '../../Service/packageEntitlements';
import EightPayModal from './components/EightPayModal/EightPayModal';
import './index.scss';

const PACKAGE_ID = 't11';

const steps = [
    {
        icon: <ShoppingOutlined />,
        title: '购买套餐',
        text: '确认套餐内容与金额，选择微信或支付宝完成支付。',
    },
    {
        icon: <WalletOutlined />,
        title: '查看我的套餐',
        text: '支付后自动展示套餐权益。再次使用时，从“我的套餐”找回。',
    },
    {
        icon: <FileTextOutlined />,
        title: '选择权益，开始创作',
        text: '按权益提示进入对应功能，完成内容设置后使用套餐解锁。',
    },
];

const faqItems = [
    {
        key: 'use',
        label: '购买后，在哪里使用套餐？',
        children:
            '支付成功后会自动打开“我的套餐”，可以查看和使用已购买的权益。也可以随时点击本页的“我的套餐”，输入套餐订单号或支付交易单号找回。',
    },
    {
        key: 'content',
        label: '套餐包含哪些内容？',
        children:
            '套餐卡片展示当前配置的内容与数量，最终以创建订单时确认的套餐信息为准。兑换权益与优惠券用途不同，请按照“我的套餐”中的提示使用。',
    },
    {
        key: 'existing',
        label: '已经购买过，是否需要再次付款？',
        children:
            '请先打开“我的套餐”查看剩余权益。如果仍有适用于当前内容的权益，可以继续使用；已使用的权益可查看其关联内容。',
    },
    {
        key: 'retrieve',
        label: '更换设备后，怎样找回套餐？',
        children:
            '在支付宝或微信账单中找到购买记录，复制订单号或交易单号，再到“我的套餐”查询。建议保存好套餐订单号。',
    },
    {
        key: 'generation',
        label: '解锁后，多久可以下载？',
        children:
            '生成时间会随文档长度、附加服务和任务数量变化。解锁后可在文章订单中查看结果，完成后下载；遇到问题可携订单号联系客服。',
    },
];

const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const price = Number(value);
    return Number.isFinite(price) && price >= 0
        ? price.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
        : null;
};

const EightPieceSet = () => {
    const [packageInfo, setPackageInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState(false);
    const buyingRef = useRef(false);
    const [searchParams, setSearchParams] = useSearchParams();
    // 只取进入页面时的那一个 ?paid=，随后把它从地址栏抹掉；抹掉后 searchParams 会变，所以不能直接依赖它
    const [paidNo] = useState(() => String(searchParams.get('paid') || '').trim());
    const setSearchParamsRef = useRef(setSearchParams);
    setSearchParamsRef.current = setSearchParams;

    // 手机支付宝付完 / 取消后带着 ?paid=订单号 回到本页：查一次单，付了就打开「我的套餐」显示兑换码
    useEffect(() => {
        if (!paidNo) return undefined;
        let active = true;
        rememberPackageOrder(paidNo);
        setSearchParamsRef.current({}, { replace: true });
        get(`/member/payment/pay/result/${encodeURIComponent(paidNo)}`, undefined, { slient: true })
            .then((res) => {
                if (!active) return;
                const paid = res?.data?.code === 0;
                if (paid) message.success('支付成功，查看并使用您的套餐');
                else message.info('暂未确认到付款，可在「我的套餐」里查看订单状态');
                NiceModal.show(GroupBuyingOrder, { initialOrderNo: paidNo, paymentCompleted: paid });
            })
            .catch(() => {
                if (!active) return;
                NiceModal.show(GroupBuyingOrder, { initialOrderNo: paidNo });
            });
        return () => {
            active = false;
        };
    }, [paidNo]);

    useEffect(() => {
        let active = true;
        get('/member/order/packages/list', undefined, { slient: true })
            .then((res) => {
                if (!active || !res.success) return;
                const packages = res.data?.packages || [];
                const current = packages
                    .flatMap((group) => group.items || [])
                    .find((item) => String(item.id) === PACKAGE_ID);
                setPackageInfo(current || null);
            })
            .catch(() => {
                // The order endpoint remains the source of truth when the catalogue is unavailable.
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    const openMyPackage = () => NiceModal.show(GroupBuyingOrder);

    const handleBuy = async () => {
        if (buyingRef.current) return;
        buyingRef.current = true;
        setBuying(true);
        try {
            const res = await post('/member/payment/order', { idf: PACKAGE_ID });
            if (!res.success || !res.data?.orderNo || formatPrice(res.data.price) === null) {
                throw new Error(res.msg || '暂时无法获取套餐订单，请稍后重试');
            }
            const { orderNo, price } = res.data;
            // Keep the order available after an H5 payment redirects away from this page.
            rememberPackageOrder(orderNo);
            const result = await NiceModal.show(EightPayModal, {
                orderNo,
                majorName: packageInfo?.title || '灵感长文八件套',
                price,
                packageType: 11,
            });
            if (result?.paid) {
                rememberPackageOrder(orderNo);
                message.success('支付成功，查看并使用您的套餐');
                NiceModal.show(GroupBuyingOrder, {
                    initialOrderNo: orderNo,
                    paymentCompleted: true,
                });
            }
        } catch (error) {
            message.error(error.message || '暂时无法创建订单，请稍后重试');
        } finally {
            buyingRef.current = false;
            setBuying(false);
        }
    };

    const price = formatPrice(packageInfo?.price);
    const contents = Array.isArray(packageInfo?.contents) ? packageInfo.contents : [];

    return (
        <>
            <div className="package-page">
                <Nav hasBottomBorder bgWhite />
                <main className="package-page__main">
                    <div className="package-page__breadcrumb">
                        <Link to="/">创作首页</Link>
                        <span>/</span>
                        <span>团购套餐</span>
                    </div>

                    <section className="package-hero" aria-labelledby="package-heading">
                        <div className="package-hero__copy">
                            <span className="package-eyebrow">
                                <GiftOutlined /> 灵感风暴 · 创作套餐
                            </span>
                            <h1 id="package-heading">
                                把创作所需，
                                <br />
                                <em>放进一个套餐。</em>
                            </h1>
                            <p className="package-hero__description">
                                从文章到配套材料，按需使用套餐权益。
                                <br className="package-desktop-break" />
                                购买、查找、使用，在这里清楚完成。
                            </p>
                            <div className="package-hero__actions">
                                <Button type="primary" size="large" href="#package-detail">
                                    查看套餐 <ArrowRightOutlined />
                                </Button>
                                <Button
                                    size="large"
                                    icon={<WalletOutlined />}
                                    onClick={openMyPackage}
                                >
                                    我的套餐
                                </Button>
                            </div>
                            <div className="package-hero__notes">
                                <span>
                                    <CheckOutlined /> 权益集中查看
                                </span>
                                <span>
                                    <CheckOutlined /> 按订单随时找回
                                </span>
                            </div>
                        </div>

                        <article
                            className="package-offer"
                            id="package-detail"
                            aria-labelledby="package-title"
                        >
                            <div className="package-offer__heading">
                                <span className="package-offer__icon">
                                    <GiftOutlined />
                                </span>
                                <div>
                                    <span className="package-overline">创作组合</span>
                                    <h2 id="package-title">
                                        {packageInfo?.title || '灵感长文八件套'}
                                    </h2>
                                </div>
                            </div>
                            <div className="package-offer__price">
                                {loading ? (
                                    <Skeleton.Input active size="large" />
                                ) : price !== null ? (
                                    <>
                                        <span>¥</span>
                                        <strong>{price}</strong>
                                        <small>/ 套</small>
                                    </>
                                ) : (
                                    <strong className="package-offer__unknown-price">
                                        购买时确认价格
                                    </strong>
                                )}
                            </div>
                            <div className="package-offer__divider" />
                            <h3>套餐包含</h3>
                            {loading ? (
                                <Skeleton active title={false} paragraph={{ rows: 4 }} />
                            ) : contents.length ? (
                                <ul className="package-offer__contents">
                                    {contents.map((item, index) => (
                                        <li key={`${item.name}-${index}`}>
                                            <span>
                                                <CheckOutlined />
                                                {item.name}
                                            </span>
                                            <b>
                                                {item.quantity}
                                                {item.unit}
                                            </b>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="package-offer__empty">
                                    套餐内容正在更新，请在购买前确认订单信息；已有套餐可直接查看权益。
                                </p>
                            )}
                            <Button
                                type="primary"
                                size="large"
                                block
                                loading={buying}
                                onClick={handleBuy}
                            >
                                购买套餐 <ArrowRightOutlined />
                            </Button>
                            <p className="package-offer__caption">
                                支付前确认实际金额 · 微信 / 支付宝
                            </p>
                        </article>
                    </section>

                    <section className="package-steps" aria-labelledby="package-steps-heading">
                        <div className="package-section-heading">
                            <span className="package-overline">从购买到使用</span>
                            <h2 id="package-steps-heading">三步，开始下一篇创作</h2>
                        </div>
                        <div className="package-steps__grid">
                            {steps.map((step, index) => (
                                <article className="package-step" key={step.title}>
                                    <div className="package-step__top">
                                        <span className="package-step__icon">{step.icon}</span>
                                        <span className="package-step__number">0{index + 1}</span>
                                    </div>
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </article>
                            ))}
                        </div>
                    </section>

                    <section className="package-existing">
                        <div className="package-existing__icon">
                            <WalletOutlined />
                        </div>
                        <div>
                            <h2>已经有套餐了？</h2>
                            <p>查看剩余权益，继续创作，或找回已使用的内容。</p>
                        </div>
                        <Button size="large" onClick={openMyPackage}>
                            打开我的套餐 <ArrowRightOutlined />
                        </Button>
                    </section>

                    <section className="package-faq" aria-labelledby="package-faq-heading">
                        <div className="package-section-heading">
                            <span className="package-overline">使用帮助</span>
                            <h2 id="package-faq-heading">您可能想了解</h2>
                            <p>关于购买、权益使用和订单找回。</p>
                        </div>
                        <Collapse items={faqItems} ghost expandIconPosition="end" />
                    </section>
                    <footer className="package-page__footer">
                        <span>灵感风暴 · 辅助创作，启发思考</span>
                        <Link to="/agreement" target="_blank" rel="noreferrer">
                            服务协议
                        </Link>
                    </footer>
                </main>
            </div>
        </>
    );
};

export default EightPieceSet;
